
import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from "react";
import { BackToTopic } from '../topics/BackToTopic';
import { deleteJob, downloadJobResultsXlsx, fetchFiles, fetchJob, resubmitJob } from '../../api/internal';
import { LoadingBar } from '../../components/LoadingBar';
import { Job, JobResult, JobResultV2, JobStatus } from '../../types/job';
import { File } from '../../types/file';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { SkypeCircleCheckIcon, SkypeCircleSlashIcon } from '@fluentui/react-icons-mdl2-branded';
import { JobResultsRendererV1 } from './SingleJob/v1';
import { JobResultsRendererV2 } from './SingleJob/v2';
const JobStatusIcon = ({ status }: { status: JobStatus }) => {
  const isSuccess = status === 'finished';
  const isFailed = status === 'failed';
  const isRunning = !(isSuccess || isFailed);
  return <>
    {isSuccess && <SkypeCircleCheckIcon className='text-green-600 text-3xl' />}
    {isFailed && <SkypeCircleSlashIcon className='text-red-600 text-3xl' />}
    {isRunning && <LoadingSpinner />}
  </>
};
export const SingleJob = () => {
  const { topicId, jobId } = useParams();
  const [job, setJob] = useState<Job | null>(null);
  const [refresh, setRefresh] = useState(0);
  const [deleteDisabled, setDeleteDisabled] = useState(false);
  const [resubmitDisabled, setResubmitDisabled] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [downloadDisabled, setDownloadDisabled] = useState(false);
  const navigate = useNavigate();
  useEffect(() => {
    if (!topicId) return;
    fetchFiles(topicId).then(setFiles);
  }, [topicId]);
  useEffect(() => {
    if (!topicId || !jobId) return;
    fetchJob(topicId, jobId)
      .then((newjob) => {
        setJob(newjob);
        if (newjob.status !== 'finished' && newjob.status !== 'failed') {
          console.log('refreshing job', newjob.status);
          setTimeout(() => setRefresh(r => r + 1), 10000);
        }
      });
  }, [topicId, jobId, refresh]);
  const deleteMyJob = async () => {
    if (!topicId || !jobId) return;
    setDeleteDisabled(true);
    await deleteJob(topicId, jobId);
    setDeleteDisabled(false);
    navigate(`/topics/${topicId}`);
  }
  const resubmitMyJob = async () => {
    if (!topicId || !jobId) return;
    setResubmitDisabled(true);
    const job = await resubmitJob(topicId, jobId);
    setResubmitDisabled(false);
    setJob(job);
    setRefresh(r => r + 1);
  }
  const downloadXlsx = async () => {
    if (!topicId || !jobId) return;
    setDownloadDisabled(true);
    await downloadJobResultsXlsx(topicId, jobId);
    setDownloadDisabled(false);
  }
  const downloadAsCsv = () => {
    if (!job || !job.results) return;
    const results = job.results;
    const csv = [];
    csv.push('page,answer');
    results?.forEach(r => {
      r.result.answer.forEach((a: string) => {
        csv.push(`${r.page},"${a}"`);
      })
    });
    const csvStr = csv.join('\n');
    const blob = new Blob([csvStr], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `job-${jobId}.csv`;
    a.click();
  };
  const downlaodBehavior = job?.results && job.results[0]?.output_version === 'extract_v2' ? downloadXlsx : downloadAsCsv;
  const ResultRenderer = job?.results && job.results[0]?.output_version === 'extract_v2' ? JobResultsRendererV2 : JobResultsRendererV1;
  const onDeleteWrapper = () => {
    setRefresh(r => r + 1);
  };
  const handleDownloadSelected = (selectedResults: JobResult<JobResultV2>[]) => {
    console.log("Selected results for download:", selectedResults);
    // Add your download logic here
  };
  const usage = job?.results?.map(r => r.usage).reduce((acc, u) => ({
    prompt_tokens: acc.prompt_tokens + u.prompt_tokens,
    completion_tokens: acc.completion_tokens + u.completion_tokens,
    total_tokens: acc.total_tokens + u.total_tokens,
  }), { prompt_tokens: 0, completion_tokens: 0, total_tokens: 0 });
  if (!topicId || !jobId || !job) return <LoadingBar />
  return (
    <>
      <BackToTopic topicId={topicId} />
      <div className="flex h-screen flex-col">
        {/* Top Section: Left and Right */}
        <div className="flex flex-1">
          {/* Left Section */}
          <div className="flex-1 flex flex-col p-4">
            <div className="text-lg p-2 bg-gray-100">
              <span className="font-bold">Question: </span>
              {job.question}
            </div>
            <div className="text-lg p-2 bg-gray-50">
              <span className="font-bold">Files used: </span>
              {job.selectedFiles.map(s => [
                files.find(f => f.id === s.fileId)?.filename,
                s.pages,
              ]).map(x => (
                <>
                  {x[0]}
                  <span className="ml-1 rounded-lg px-1 text-sm text-white bg-gray-400">
                    Page(s): {x[1] === '*' ? 'all' : x[1]}
                  </span>
                </>
              ))}
            </div>
            <div className="text-lg p-2 bg-gray-100">
              <span className="font-bold">Language model: </span>
              {job.llm}
            </div>
            <div className="text-lg p-2 bg-gray-50">
              <span className="font-bold">Job status: </span>
              <span className="inline-block">
                <JobStatusIcon status={job.status} />
              </span>
              {job.status}
              {job.error ? <div className="text-red-600">Error: {job.error}</div> : null}
            </div>
            {usage ? (
              <div className="text-lg p-2 bg-gray-100">
                <div className="font-bold">Usage: </div>
                <div className="text-sm">Prompt tokens: {usage.prompt_tokens}</div>
                <div className="text-sm">Completion tokens: {usage.completion_tokens}</div>
                <div className="text-sm">Total tokens: {usage.total_tokens}</div>
              </div>
            ) : null}
          </div>
          {/* Right Section */}
          <div className="flex-0 flex flex-col w-1/8 h-1/4 p-4 justify-between">
            <button
              className="mb-4 btn border border-green-600 text-green-800 p-1 rounded-md text-center hover:bg-green-100"
              onClick={downlaodBehavior}
              disabled={downloadDisabled}
            >
              {downloadDisabled ? <LoadingSpinner inline={true} /> : 'Download to Excel'}
            </button>
            <button
              className="mb-4 btn border border-green-600 text-green-800 p-1 rounded-md text-center hover:bg-green-100"
              disabled={resubmitDisabled}
              onClick={resubmitMyJob}
            >
              {resubmitDisabled ? <LoadingSpinner inline={true} /> : 'Resubmit this job'}
            </button>
            <button
              className="btn border border-red-900 bg-red-700 text-white p-1 rounded-md hover:bg-red-600"
              disabled={deleteDisabled}
              onClick={deleteMyJob}
            >
              {deleteDisabled ? '...' : 'Delete this job'}
            </button>
          </div>
        </div>
        {/* Bottom Section */}
        <div className="flex flex-col p-4">
          <div className="text-lg font-bold mb-2">Results:</div>
          {job.results ? (
            <ResultRenderer
              results={job.results}
              files={files}
              onDelete={onDeleteWrapper}
              onDownloadSelected={handleDownloadSelected}
            />
          ) : (
            <div>No results available.</div>
          )}
        </div>
      </div>
    </>
  );
};
